import React, { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, useMemo } from "react";
import { auth, api } from "./api";
import { toast } from "sonner";
import { playOrderSound } from "./sound";

/**
 * High-performance tick store.
 *
 * Architecture:
 *   • A single mutable Map holds all ticks (no React reconciliation per tick).
 *   • Per-symbol subscribers (Map<symbol, Set<listener>>) are notified ONLY when
 *     that one symbol's LTP/bid/ask changes.
 *   • A global version counter ticks once per WS batch — used by consumers who
 *     genuinely need the full list (Dashboard list, AdminMarketWatch).
 *
 * Why this matters:
 *   • OrderModal / individual scrip cards subscribe to ONE symbol → they don't
 *     rerender when other 483 symbols tick.
 *   • Filtered-list computations only run when version changes, not when a
 *     single keystroke triggers re-render of the parent.
 *
 * Public hooks (keep the API surface backward-compatible):
 *   useTicks()            – { ticks, connected }  (live snapshot reference; rare consumers)
 *   useTick(symbol)       – returns just that symbol's tick (RECOMMENDED)
 *   useTickVersion()      – integer counter; bump per WS batch (for memo deps)
 *   useTickConnected()    – just the connection bool (rarely changes)
 */

const TickContext = createContext(null);
export const useTicks = () => useContext(TickContext);

// External tick store (singleton-per-provider)
function createTickStore() {
  const data = new Map();                    // symbol -> tick obj
  const perSymbolListeners = new Map();      // symbol -> Set<() => void>
  const globalListeners = new Set();         // () => void  (snapshot version bumps)
  let version = 0;

  const subscribeSymbol = (symbol, listener) => {
    let set = perSymbolListeners.get(symbol);
    if (!set) { set = new Set(); perSymbolListeners.set(symbol, set); }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) perSymbolListeners.delete(symbol);
    };
  };

  const subscribeGlobal = (listener) => {
    globalListeners.add(listener);
    return () => globalListeners.delete(listener);
  };

  const getSymbol = (symbol) => data.get(symbol);
  const getAll = () => data;
  const getVersion = () => version;

  const seed = (items) => {
    for (const s of items || []) {
      if (s && s.symbol) data.set(s.symbol, s);
    }
    version++;
    globalListeners.forEach((fn) => fn());
    // Don't notify per-symbol — seed is initial snapshot, components mount fresh
  };

  const applyBatch = (items) => {
    const touched = [];
    for (const t of items) {
      const old = data.get(t.symbol);
      // Direction marker for flash animations
      if (old) {
        if (t.ltp != null && t.ltp !== old.ltp) {
          t._dir = t.ltp > old.ltp ? "up" : "down";
        } else {
          t._dir = old._dir || "none";
        }
        data.set(t.symbol, { ...old, ...t });
      } else {
        data.set(t.symbol, t);
      }
      touched.push(t.symbol);
    }
    version++;
    // Notify ONLY changed symbols
    for (const sym of touched) {
      const set = perSymbolListeners.get(sym);
      if (set) set.forEach((fn) => fn());
    }
    // Bump global listeners once per batch
    globalListeners.forEach((fn) => fn());
  };

  return { subscribeSymbol, subscribeGlobal, getSymbol, getAll, getVersion, seed, applyBatch };
}

let _storeSingleton = null;
const getStore = () => {
  if (!_storeSingleton) _storeSingleton = createTickStore();
  return _storeSingleton;
};

/** Hook: subscribe to ONE symbol's ticks. Doesn't rerender when other symbols change. */
export function useTick(symbol) {
  const store = getStore();
  return useSyncExternalStore(
    (cb) => (symbol ? store.subscribeSymbol(symbol, cb) : () => {}),
    () => (symbol ? store.getSymbol(symbol) : null),
    () => null,
  );
}

/** Hook: integer that bumps once per WS batch. Use as a dependency in useMemo
 *  for list-style consumers (Dashboard, AdminMarketWatch). Avoids passing
 *  the entire ticks object around as a memo dependency. */
export function useTickVersion() {
  const store = getStore();
  return useSyncExternalStore(
    (cb) => store.subscribeGlobal(cb),
    () => store.getVersion(),
    () => 0,
  );
}

/** Hook: access the raw store data (read-only). Read inside a useMemo gated
 *  on useTickVersion() to avoid stale data. */
export function useTickStore() {
  return getStore();
}

/** Hook: subscribe ONLY to the WS-connection bool. Doesn't re-render on tick
 *  batches. Use this in header components (Layout / MobileLayout / BroadcastBar)
 *  that only need the green/red "Live ticks connected" pill — they would
 *  otherwise re-render once per second because the legacy `useTicks()` context
 *  value changes per batch.
 */
const connectedStore = {
  value: false,
  listeners: new Set(),
  set(v) {
    if (this.value === v) return;
    this.value = v;
    this.listeners.forEach((fn) => fn());
  },
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  },
  get() { return this.value; },
};
export function useTickConnected() {
  return useSyncExternalStore(
    (cb) => connectedStore.subscribe(cb),
    () => connectedStore.get(),
    () => false,
  );
}

export function TickProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const store = getStore();

  const handleEvent = (ev) => {
    const me = auth.getUser ? auth.getUser() : null;
    const isAdmin = me?.role === "admin";
    if (!isAdmin && me && ev.client_id && me.client_id !== ev.client_id) return;
    const who = isAdmin && ev.client_id ? `${ev.client_id} — ` : "";
    if (ev.kind === "order_executed") {
      playOrderSound("execute");
      toast.success(
        `${who}Order Executed`,
        {
          description: `${ev.side} ${ev.qty_lots} lot${ev.qty_lots !== 1 ? "s" : ""} ${ev.symbol} @ ${ev.price} (${ev.order_type})`,
          duration: 3000,
        }
      );
    } else if (ev.kind === "order_rejected") {
      playOrderSound("reject");
      toast.error(
        `${who}Order Rejected`,
        {
          description: `${ev.side} ${ev.qty_lots} lot${ev.qty_lots !== 1 ? "s" : ""} ${ev.symbol}${ev.reason ? " — " + ev.reason : ""}`,
          duration: 3000,
        }
      );
    } else if (ev.kind === "oauth_expiring" && isAdmin) {
      toast.warning("🔑 Shoonya Re-Authorize Soon", {
        description: ev.message || "Token expiring — go to Admin → System → Re-Authorize",
        duration: 10000,
      });
    } else if (ev.kind === "oauth_expired" && isAdmin) {
      toast.error("🔴 Shoonya OAuth EXPIRED", {
        description: ev.message || "Live feed down — re-authorize NOW",
        duration: 30000,
      });
    }
  };

  useEffect(() => {
    if (!auth.getToken()) return;
    // Seed initial state from REST
    api.get("/symbols").then((r) => {
      store.seed(r.data.items || []);
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[ticks] failed to seed /symbols:", err?.message || err);
    });
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const tok = auth.getToken();
    const wsUrl = backend.replace(/^http/, "ws") + "/api/ws/ticks"
                  + (tok ? `?token=${encodeURIComponent(tok)}` : "");
    let stopped = false;
    let timer;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { setConnected(true); connectedStore.set(true); };
        ws.onclose = (ev) => {
          setConnected(false);
          connectedStore.set(false);
          // 4403 = server signalled this session was replaced by a new login
          // elsewhere. Don't reconnect — force a clean logout so the API
          // interceptor's 401 redirect path is triggered.
          if (ev && ev.code === 4403) {
            try {
              sessionStorage.setItem("aditi_logout_reason",
                "Your account was logged in from another device.");
            } catch (_) { /* sessionStorage may be blocked */ }
            try { localStorage.removeItem("aditi5_token"); localStorage.removeItem("aditi5_user"); } catch (_) {}
            window.location.href = "/login";
            return;
          }
          if (!stopped) timer = setTimeout(connect, 2000);
        };
        ws.onerror = (err) => {
          // eslint-disable-next-line no-console
          console.warn("[WS] error event:", err);
        };
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "snapshot" || msg.type === "ticks") {
              store.applyBatch(msg.data || []);
            } else if (msg.type === "event" && msg.event) {
              handleEvent(msg.event);
            }
          } catch (_) {}
        };
      } catch (_) {
        timer = setTimeout(connect, 2000);
      }
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      try { wsRef.current?.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Perf: instead of rebuilding a 484-key object every WS batch (a per-second
  // allocation that was triggering GC pressure), we expose a Proxy that reads
  // lazily from the live Map. Identity changes on each version bump so consumers
  // using `[ticks]` as a memo dep still re-run their memos, but the heavy
  // Object copy is gone.
  const version = useTickVersion();
  const ticksSnapshot = useMemo(() => {
    const map = store.getAll();
    return new Proxy({}, {
      get: (_, prop) => {
        if (typeof prop !== "string") return undefined;
        const v = map.get(prop);
        return v;
      },
      has: (_, prop) => typeof prop === "string" && map.has(prop),
      ownKeys: () => Array.from(map.keys()),
      getOwnPropertyDescriptor: (_, prop) => {
        if (typeof prop !== "string") return undefined;
        const v = map.get(prop);
        if (v === undefined) return undefined;
        return { enumerable: true, configurable: true, writable: false, value: v };
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const ctxValue = useMemo(() => ({ ticks: ticksSnapshot, connected }), [ticksSnapshot, connected]);

  return (
    <TickContext.Provider value={ctxValue}>
      {children}
    </TickContext.Provider>
  );
}
